
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Clock, CheckSquare, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BoardDashboard = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Board Dashboard</h1>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/users")}>Manage Users</Button>
          <Button onClick={() => navigate("/settings")}>Settings</Button>
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Families</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">24</div>
            <p className="text-xs text-muted-foreground">
              2 new this month
            </p>
            <div className="mt-2">
              <div className="flex items-center">
                <Users className="h-4 w-4 text-muted-foreground mr-1" />
                <span className="text-xs text-muted-foreground">12 active today</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Work Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">82%</div>
            <p className="text-xs text-muted-foreground">
              Completion rate
            </p>
            <div className="mt-2">
              <Progress value={82} className="h-2" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">18</div>
            <p className="text-xs text-muted-foreground">
              8 pending approval
            </p>
            <div className="mt-2">
              <div className="flex items-center">
                <CheckSquare className="h-4 w-4 text-muted-foreground mr-1" />
                <span className="text-xs text-muted-foreground">12 completed this week</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Calendar</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">4</div>
            <p className="text-xs text-muted-foreground">
              Events this week
            </p>
            <div className="mt-2">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-muted-foreground mr-1" />
                <span className="text-xs text-muted-foreground">Next: Board Meeting (Thu)</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="families">Families</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cooperative Health</CardTitle>
              <CardDescription>Overall participation metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Parent Workday Participation</span>
                    <span>88%</span>
                  </div>
                  <Progress value={88} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Task Completion Rate</span>
                    <span>76%</span>
                  </div>
                  <Progress value={76} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Meeting Attendance</span>
                    <span>92%</span>
                  </div>
                  <Progress value={92} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Documentation Completion</span>
                    <span>64%</span>
                  </div>
                  <Progress value={64} className="h-2" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Events</CardTitle>
                <CardDescription>Next 7 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-medium">Board Meeting</h3>
                      <p className="text-sm text-muted-foreground">Thursday, 7:00 PM</p>
                    </div>
                    <Badge variant="outline">Board</Badge>
                  </div>
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-medium">Parent Orientation</h3>
                      <p className="text-sm text-muted-foreground">Friday, 6:30 PM</p>
                    </div>
                    <Badge variant="outline">All</Badge>
                  </div>
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-medium">Garden Clean-up</h3>
                      <p className="text-sm text-muted-foreground">Saturday, 9:00 AM</p>
                    </div>
                    <Badge variant="outline">Volunteer</Badge>
                  </div>
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-medium">Teacher Planning Day</h3>
                      <p className="text-sm text-muted-foreground">Monday, All Day</p>
                    </div>
                    <Badge variant="outline">Staff</Badge>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full">View Calendar</Button>
              </CardFooter>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Alerts & Notices</CardTitle>
                <CardDescription>Issues needing attention</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-destructive/10 rounded-md border border-destructive/20">
                    <h3 className="font-medium text-destructive">Under-scheduled Families</h3>
                    <p className="text-sm">3 families are under their required work hour commitments</p>
                    <div className="mt-2">
                      <Button size="sm" variant="outline" className="text-destructive border-destructive/50">
                        Review
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-amber-500/10 rounded-md border border-amber-500/20">
                    <h3 className="font-medium text-amber-600">Background Check Renewals</h3>
                    <p className="text-sm">5 parents need to renew their background checks</p>
                    <div className="mt-2">
                      <Button size="sm" variant="outline" className="text-amber-600 border-amber-500/50">
                        Notify
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-primary/10 rounded-md border border-primary/20">
                    <h3 className="font-medium text-primary">Budget Review</h3>
                    <p className="text-sm">Quarterly budget review is due by end of month</p>
                    <div className="mt-2">
                      <Button size="sm" variant="outline" className="text-primary border-primary/50">
                        Prepare Report
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="tasks">
          <Card>
            <CardHeader>
              <CardTitle>Task Management</CardTitle>
              <CardDescription>Overview of all cooperative tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Facilities Maintenance</h3>
                    <p className="text-sm text-muted-foreground">11 tasks, 6 assigned</p>
                  </div>
                  <Badge>75% Complete</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Classroom Support</h3>
                    <p className="text-sm text-muted-foreground">8 tasks, 8 assigned</p>
                  </div>
                  <Badge>62% Complete</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Administrative</h3>
                    <p className="text-sm text-muted-foreground">6 tasks, 4 assigned</p>
                  </div>
                  <Badge>83% Complete</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Events & Fundraising</h3>
                    <p className="text-sm text-muted-foreground">15 tasks, 12 assigned</p>
                  </div>
                  <Badge>40% Complete</Badge>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button className="w-full" onClick={() => navigate("/tasks")}>Manage Tasks</Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="families">
          <Card>
            <CardHeader>
              <CardTitle>Family Participation</CardTitle>
              <CardDescription>Tracking participation across all families</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="rounded-md border">
                  <div className="p-3 bg-muted/50">
                    <div className="grid grid-cols-4 gap-4 font-medium text-sm">
                      <div>Family</div>
                      <div>Work Hours</div>
                      <div>Tasks</div>
                      <div>Status</div>
                    </div>
                  </div>
                  <div className="divide-y">
                    {[
                      { name: "Johnson Family", hours: "18/20", tasks: "12/15", status: "On Track" },
                      { name: "Miller Family", hours: "15/20", tasks: "10/15", status: "At Risk" },
                      { name: "Garcia Family", hours: "20/20", tasks: "15/15", status: "Complete" },
                      { name: "Wilson Family", hours: "12/20", tasks: "8/15", status: "Behind" },
                      { name: "Lee Family", hours: "19/20", tasks: "14/15", status: "On Track" },
                    ].map((family, i) => (
                      <div key={i} className="p-3">
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>{family.name}</div>
                          <div>{family.hours}</div>
                          <div>{family.tasks}</div>
                          <div>
                            <Badge variant={
                              family.status === "Complete" ? "default" :
                              family.status === "On Track" ? "outline" :
                              family.status === "At Risk" ? "secondary" :
                              "destructive"
                            }>
                              {family.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full" onClick={() => navigate("/users")}>View All Families</Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BoardDashboard;
